import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { UNIT_LABEL, fmtNum } from "./kitchenUtils";

const EMPTY = { name: "", category: "", measure: "count", unit: "each", count_label: "", pack_label: "", pack_qty: "", pack_cost: "", vendor: "", notes: "" };
const MEASURE_HINT = { count: "slices, pieces, eggs", volume: "liquid eggs, milk, sauce", weight: "meat, cheese, flour" };

const IngredientForm = ({ initial, units, onSaved, onCancel }) => {
  const [f, setF] = useState({ ...EMPTY, ...initial, pack_qty: initial?.pack_qty ?? "", pack_cost: initial?.pack_cost ?? "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setMeasure = (m) => setF((s) => ({ ...s, measure: m, unit: { count: "each", volume: "quart", weight: "lb" }[m] }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, pack_qty: f.pack_qty === "" ? null : Number(f.pack_qty), pack_cost: f.pack_cost === "" ? null : Number(f.pack_cost) };
    delete body.id; delete body.created_at; delete body.updated_at;
    try {
      const { data } = initial?.id ? await api.put(`/admin/kitchen/ingredients/${initial.id}`, body) : await api.post("/admin/kitchen/ingredients", body);
      onSaved(data.ingredient);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save ingredient.");
    } finally {
      setBusy(false);
    }
  };
  const cls = "h-10 rounded-xl bg-white border-black/10";
  return (
    <form onSubmit={submit} data-testid="ingredient-form" className="rounded-2xl border border-brand-teal/40 bg-white p-4 grid sm:grid-cols-6 gap-2">
      <Input required placeholder="Ingredient name" value={f.name} onChange={set("name")} data-testid="ingredient-name" className={`sm:col-span-3 ${cls}`} />
      <Input placeholder="Category (Meat, Dairy…)" value={f.category} onChange={set("category")} data-testid="ingredient-category" className={`sm:col-span-3 ${cls}`} />
      <div className="sm:col-span-6 flex flex-wrap gap-1.5" role="radiogroup">
        {Object.keys(units).map((m) => (
          <button key={m} type="button" role="radio" aria-checked={f.measure === m} onClick={() => setMeasure(m)} data-testid={`ingredient-measure-${m}`}
            className={`rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] border ${f.measure === m ? "bg-brand-charcoal text-white border-brand-charcoal" : "border-black/10 text-brand-charcoal/70"}`}>
            By {m} <span className="normal-case font-normal opacity-70">({MEASURE_HINT[m]})</span>
          </button>
        ))}
      </div>
      <select value={f.unit} onChange={set("unit")} data-testid="ingredient-unit" className="sm:col-span-2 h-10 rounded-xl border border-black/10 px-2 text-[13px]">
        {units[f.measure].map((u) => <option key={u} value={u}>Shop/track in {UNIT_LABEL[u]}</option>)}
      </select>
      {f.measure === "count" && <Input placeholder="Counted as (slices, pieces)" value={f.count_label} onChange={set("count_label")} data-testid="ingredient-count-label" className={`sm:col-span-4 ${cls}`} />}
      <Input placeholder="Pack name (case, bag, carton)" value={f.pack_label} onChange={set("pack_label")} data-testid="ingredient-pack-label" className={`sm:col-span-2 ${cls}`} />
      <Input type="number" step="any" min="0" placeholder={`${UNIT_LABEL[f.unit]} per pack`} value={f.pack_qty} onChange={set("pack_qty")} data-testid="ingredient-pack-qty" className={`sm:col-span-2 ${cls}`} />
      <Input type="number" step="0.01" min="0" placeholder="Cost per pack $" value={f.pack_cost} onChange={set("pack_cost")} data-testid="ingredient-pack-cost" className={`sm:col-span-2 ${cls}`} />
      <Input placeholder="Vendor" value={f.vendor} onChange={set("vendor")} data-testid="ingredient-vendor" className={`sm:col-span-3 ${cls}`} />
      <div className="sm:col-span-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-10 px-4 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60">Cancel</button>
        <button type="submit" disabled={busy} data-testid="ingredient-save" className="h-10 px-5 rounded-full bg-brand-charcoal text-white text-[11px] font-bold uppercase tracking-[0.1em] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save ingredient"}
        </button>
      </div>
    </form>
  );
};

export const IngredientsTab = ({ units }) => {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/admin/kitchen/ingredients").then((r) => setList(r.data.ingredients)); }, []);
  const saved = (ing) => {
    setList((prev) => [...prev.filter((x) => x.id !== ing.id), ing].sort((a, b) => a.name.localeCompare(b.name)));
    setEditing(null);
  };
  const remove = async (ing) => {
    if (!window.confirm(`Delete ${ing.name}?`)) return;
    try {
      await api.delete(`/admin/kitchen/ingredients/${ing.id}`);
      setList((prev) => prev.filter((x) => x.id !== ing.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't delete.");
    }
  };
  if (!list) return <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />;
  return (
    <div className="space-y-3" data-testid="ingredients-tab">
      {editing ? (
        <IngredientForm initial={editing} units={units} onSaved={saved} onCancel={() => setEditing(null)} />
      ) : (
        <button type="button" onClick={() => setEditing({})} data-testid="ingredient-add" className="h-11 px-5 rounded-full bg-brand-charcoal text-white text-[11.5px] font-bold uppercase tracking-[0.1em] inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add raw ingredient
        </button>
      )}
      {list.length === 0 && <p className="text-[13px] text-brand-charcoal/55">No ingredients yet. Add things like liquid eggs, turkey bacon, Texas toast.</p>}
      <div className="grid gap-2">
        {list.map((ing) => (
          <div key={ing.id} data-testid={`ingredient-row-${ing.id}`} className="flex items-center gap-3 rounded-xl bg-white border border-black/10 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[14px] text-brand-charcoal">{ing.name} <span className="font-normal text-brand-charcoal/50 text-[12px]">· {ing.category || "Uncategorized"}</span></p>
              <p className="text-[12px] text-brand-charcoal/60">
                By {ing.measure} · tracked in {ing.measure === "count" && ing.count_label ? ing.count_label : UNIT_LABEL[ing.unit]}
                {ing.pack_qty ? ` · ${ing.pack_label || "pack"} = ${fmtNum(ing.pack_qty)} ${ing.unit === "each" && ing.count_label ? ing.count_label : UNIT_LABEL[ing.unit]}` : ""}
                {ing.pack_cost != null ? ` · $${Number(ing.pack_cost).toFixed(2)}/${ing.pack_label || "pack"}` : ""}
                {ing.vendor ? ` · ${ing.vendor}` : ""}
              </p>
            </div>
            <button type="button" onClick={() => setEditing(ing)} data-testid={`ingredient-edit-${ing.id}`} className="p-2 rounded-full hover:bg-brand-cream" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
            <button type="button" onClick={() => remove(ing)} data-testid={`ingredient-delete-${ing.id}`} className="p-2 rounded-full hover:bg-red-50 text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IngredientsTab;
